import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  FormControlLabel, IconButton, InputAdornment, Paper, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  surgicalKitsApi, type SurgicalKitRowDto, type SurgicalKitItemInput, type ItemDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { ItemPicker } from "@/components/Pickers";

const QUERY_KEY = "surgical-kits";

/**
 * UC-FN-HOS-02 — surgical kit (OT BOM) master. A kit is the template an OT consumption starts
 * from: pick the kit on the theatre form and its items become the lines.
 */
export function SurgicalKitsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const canManage = useAuth(s => s.hasPermission("hospital.surgicalkit.manage"));

  const query = useQuery({
    queryKey: [QUERY_KEY, search, pagination.page, pagination.pageSize],
    queryFn: () => surgicalKitsApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Surgical kit deleted" },
    mutationFn: (id: string) => surgicalKitsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });

  const columns: GridColDef<SurgicalKitRowDto>[] = [
    { field: "code", headerName: "Code", width: 140 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
    { field: "procedureCode", headerName: "Procedure", width: 160,
      renderCell: (p) => p.row.procedureCode ?? "—" },
    { field: "itemCount", headerName: "Items", width: 90, type: "number",
      renderCell: (p) => p.row.itemCount === 0
        // A kit with no items loads nothing onto the theatre form — worth flagging here.
        ? <Chip size="small" color="warning" label="empty" />
        : <span>{p.row.itemCount}</span> },
    { field: "isActive", headerName: "Status", width: 120,
      renderCell: (p) => <StatusPill label={p.row.isActive ? "Active" : "Inactive"} /> },
    {
      field: "actions", headerName: "", width: 100, sortable: false, filterable: false, align: "right",
      renderCell: (p) => canManage ? (
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={() => { setEditingId(p.row.id); setFormOpen(true); }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error"
            onClick={() => {
              if (confirm(`Delete kit "${p.row.code}"? Blocked once an OT consumption has used it.`))
                deleteMutation.mutate(p.row.id);
            }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : null,
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Hospital"]}
        title="Surgical kits"
        subtitle="OT bill of materials — the consumables and implants a procedure needs. Picking a kit on an OT consumption loads its items as lines."
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setEditingId(null); setFormOpen(true); }}>
            New kit
          </Button>
        ) : undefined}
      />

      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code, name or procedure code"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 560 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage && (setEditingId(p.row.id), setFormOpen(true))}
          />
        </Box>
      </Paper>

      <SurgicalKitDialog open={formOpen} onClose={() => setFormOpen(false)} id={editingId} />
    </Box>
  );
}

// =============================== Create / edit ===============================

interface DraftItem { key: string; item: ItemDto | null; qty: number; isImplant: boolean }
const blankItem = (): DraftItem => ({ key: crypto.randomUUID(), item: null, qty: 1, isImplant: false });

function SurgicalKitDialog({ open, onClose, id }: { open: boolean; onClose: () => void; id: string | null }) {
  const qc = useQueryClient();
  const isEdit = !!id;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [procedureCode, setProcedureCode] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState<DraftItem[]>([blankItem()]);

  const detail = useQuery({
    queryKey: [QUERY_KEY, "detail", id],
    queryFn: () => surgicalKitsApi.get(id!),
    enabled: open && isEdit,
  });

  useEffect(() => {
    if (!open) return;
    if (!isEdit) {
      setCode(""); setName(""); setProcedureCode(""); setIsActive(true); setItems([blankItem()]);
      return;
    }
    const d = detail.data;
    if (!d) return;
    setCode(d.code); setName(d.name); setProcedureCode(d.procedureCode ?? ""); setIsActive(d.isActive);
    setItems(d.items.length === 0 ? [blankItem()] : d.items.map(i => ({
      key: crypto.randomUUID(),
      item: { id: i.itemId, sku: i.itemSku, name: i.itemName } as ItemDto,
      qty: i.qty, isImplant: i.isImplant,
    })));
  }, [open, isEdit, detail.data]);

  const usable = items.filter(i => i.item && i.qty > 0);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Surgical kit updated" : "Surgical kit created" },
    mutationFn: async () => {
      const payload = {
        code: code.trim(), name: name.trim(),
        procedureCode: procedureCode.trim() || null,
        items: usable.map((i): SurgicalKitItemInput => ({
          itemId: i.item!.id, qty: i.qty, isImplant: i.isImplant,
        })),
      };
      if (isEdit && id) { await surgicalKitsApi.update(id, { ...payload, isActive }); return id; }
      const r = await surgicalKitsApi.create(payload);
      return r.id;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [QUERY_KEY] }); onClose(); },
  });

  const update = (key: string, patch: Partial<DraftItem>) =>
    setItems(prev => prev.map(i => i.key === key ? { ...i, ...patch } : i));

  const err = !code.trim() ? "Code required"
    : !name.trim() ? "Name required"
    : usable.length === 0 ? "Add at least one item — an empty kit loads nothing onto the OT form"
    : null;
  const loading = isEdit && detail.isLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          {isEdit ? "Edit surgical kit" : "New surgical kit"}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          {detail.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(detail.error)}</Alert>}
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
          ) : (
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField label="Code *" size="small" sx={{ width: 180 }} autoFocus
                  value={code} onChange={(e) => setCode(e.target.value)} />
                <TextField label="Name *" size="small" fullWidth
                  value={name} onChange={(e) => setName(e.target.value)} />
                <TextField label="Procedure code" size="small" sx={{ width: 200 }}
                  value={procedureCode} onChange={(e) => setProcedureCode(e.target.value)} />
              </Stack>
              {isEdit && (
                <FormControlLabel label="Active"
                  control={<Switch checked={isActive} onChange={(_, v) => setIsActive(v)} />} />
              )}

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">Kit contents</Typography>
                <Button size="small" startIcon={<AddIcon />}
                  onClick={() => setItems(prev => [...prev, blankItem()])}>
                  Add item
                </Button>
              </Stack>
              <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 300 }}>Item *</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>Qty *</TableCell>
                    <TableCell sx={{ width: 110 }}>Implant?</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((i, idx) => (
                    <TableRow key={i.key}>
                      <TableCell>
                        <ItemPicker value={i.item} label={`Item ${idx + 1}`} required error={!i.item}
                          onChange={(v) => update(i.key, { item: v })} />
                      </TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" sx={{ width: 90 }} value={i.qty}
                          inputProps={{ min: 0, step: "any", style: { textAlign: "right" } }}
                          onChange={(e) => update(i.key, { qty: Number(e.target.value) || 0 })} />
                      </TableCell>
                      <TableCell>
                        {/* Implant lines make the theatre form demand a UDI per unit issued. */}
                        <Switch size="small" checked={i.isImplant}
                          onChange={(_, v) => update(i.key, { isImplant: v })} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" disabled={items.length === 1}
                          onClick={() => setItems(prev => prev.filter(x => x.key !== i.key))}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </TableContainer>

              {err && <Alert severity="info">{err}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || loading || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
